/**
 * Child Task List Component
 *
 * Displays and manages child tasks for a parent task.
 * Supports reordering, editing, and removal.
 */

import { Button } from '../../ui/button';
import { GripVertical, Trash2, Pencil } from 'lucide-react';
import type { ScheduledTask } from '../../../lib/api';

interface ChildTaskListProps {
  childTasks: ScheduledTask[];
  parentTaskId: string;
  onMoveChild: (childId: string, direction: 'up' | 'down') => void;
  onRemoveChild: (childId: string) => void;
  onEditChild: (childId: string) => void;
}

export function ChildTaskList({ childTasks, parentTaskId, onMoveChild, onRemoveChild, onEditChild }: ChildTaskListProps) {
  if (childTasks.length === 0) {
    return (
      <div className="text-center py-4 border rounded-lg bg-muted/30">
        <p className="text-sm text-muted-foreground">
          No child tasks yet. Click "Add" to create one.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg divide-y">
      {childTasks.map((child, index) => (
        <div
          key={child.id}
          className="flex items-center gap-2 p-2 hover:bg-muted/30"
        >
          <div className="flex flex-col">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0"
              disabled={index === 0}
              onClick={() => onMoveChild(child.id, 'up')}
            >
              <span className="sr-only">Move up</span>
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0"
              disabled={index === childTasks.length - 1}
              onClick={() => onMoveChild(child.id, 'down')}
            >
              <span className="sr-only">Move down</span>
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
          </div>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground font-mono text-xs w-5">
            {index + 1}.
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{child.name}</div>
            <div className="text-xs text-muted-foreground">
              {child.jobType === 'Sql.EntitySync' && 'SQL Sync'}
              {child.jobType === 'Sql.RefreshMetadata' && 'Metadata Refresh'}
              {!child.jobType && 'Entity Mapping'}
              {typeof child.parameters?.entityType === 'string' ? `: ${child.parameters.entityType}` : null}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onEditChild(child.id)}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onRemoveChild(child.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="sr-only">Remove</span>
          </Button>
        </div>
      ))}
    </div>
  );
}
